import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Legend from '@molecules/pursuitLeader/components/legend';
import {
  RowStyle,
  LegendTextStyle,
  LegendFutureContainer,
  LabelStyle,
  StyledSpanContainer,
} from '@atoms/sidebar/sidebar.styles';
import DropDownSelect from '@atoms/dropDownSelect/dropDownSelect';
import { CurrentPill, FuturePillDashboard } from '@constants/pursuitLeaderConstants';
import {
  QuarterDetailsContainer,
  MonthlyTitleContainer,
  TitleContainer,
  SubHeaderContainer,
  StyledHeader,
  ContainerWrapper,
} from '@atoms/dashboard/quarterlyDetails.styles';
import flagIcon from '@assets/icons/forecastIconBlack.svg';
import { MotifButton } from '@ey-xd/motif-react';
import { useRecoilValue } from 'recoil';
import { Current_User, Recoil_Quarter_State, System_Date_Time_State } from '@recoil/recoil';
import { formatNumberWithKAndM, trimText } from '@util/commonUtil';
import {
  BUTTON_TITLE,
  MONTH_QUARTER_STATUS,
  QUARTERLY_DETAILS,
  ROLE_CONST,
  QUARTER_OPTIONS_LIST,
  SOURCE,
} from '@constants/commonConstants';
import {
  getDashboardAllQuartersData,
  getDashboardSingleQuartersData,
} from '@services/commonServices';
import MonthlyDetails from '../../../molecules/gcsp/components/dashboard/montlyDetails';
import { appInsights } from '@services/appInsightsService';

const QuarterDetails = () => {
  const navigate = useNavigate();
  const [quarterYearRendering, setQuarterYearRendering] = useState(null);
  const quarterInfo = useRecoilValue(Recoil_Quarter_State);
  const currentUser = useRecoilValue(Current_User);
  const systemDateTime = useRecoilValue(System_Date_Time_State);

  let currentYear;
  let quarterYearHeaderList;
  const yearOptionsList = [];

  const buttonStyle = {
    width: 160,
    height: 44,
    fontWeight: 400,
  };

  if (quarterInfo !== null) {
    currentYear = quarterInfo.fiscal_year;
    // currentYear = 2030;

    for (let i = currentYear - 4; i <= parseInt(currentYear, 10) + 1; i++) {
      // Dropdown Conditions
      // 1. Max five years and miniumum two years are allowed.
      // 2. List does not have year less than 2024.
      // 3. Max value permitted is current year + 1.
      // i.e.
      // 2024:- 2024, 2025
      // 2025:- 2024, 2025, 2026
      // 2026:- 2024, 2025, 2026, 2027
      // 2027:- 2024, 2025, 2026, 2027, 2028
      // 2028:- 2025, 2026, 2027, 2028, 2029
      if (i >= 2024 && i > parseInt(currentYear, 10) - 4) {
        yearOptionsList.push({ value: i.toString(), label: i.toString() });
      }
    }

    quarterYearHeaderList = QUARTER_OPTIONS_LIST.map((quarter, index) => ({
      id: index,
      quarterText: quarter.label,
      yearText: currentYear,
      data: null,
    }));
  }

  // Added this because commit value is not getting modified after submit forecast
  if (sessionStorage.getItem('refresh_page')) {
    sessionStorage.removeItem('refresh_page');
    window.location.reload(); // To refresh the Page
  }

  useEffect(() => {
    if (quarterInfo !== null) {
      if (quarterYearRendering === null) {
        setQuarterYearRendering(quarterYearHeaderList);
      }
      appInsights.trackTrace({
        message: JSON.stringify({
          message: `Calling getDashboardAllQuartersData with reqPayload : ${currentUser.email} ${currentYear} ${systemDateTime.split('T')[0]} ${ROLE_CONST.PL} 0b25704d-5806-ee11-8f6d-002248e5e692`,
          userId: localStorage.getItem('userEmail'),
          source: SOURCE.FRONTEND,
        }),
        severityLevel: 1, // 1 represents response log
      });

      const fetchData = async () => {
        const dashboardAllQuarterData = await getDashboardAllQuartersData(
          currentUser.email,
          currentYear,
          systemDateTime.split('T')[0],
          ROLE_CONST.PL,
          '0b25704d-5806-ee11-8f6d-002248e5e692', // to be change
        );
        modifyQuarterYearRendering(dashboardAllQuarterData);
      };
      fetchData();
    }
  }, [quarterInfo]);

  const modifyQuarterYearRendering = allQuarterData => {
    appInsights.trackTrace({
      message: JSON.stringify({
        message: `Entering modifyQuarterYearRendering with payload : ${JSON.stringify(allQuarterData)}`,
        userId: localStorage.getItem('userEmail'),
        source: SOURCE.FRONTEND,
      }),
      severityLevel: 1, // 1 represents response log
    });
    // Adding data in list of quarter and year
    const updatedQuarterYearHeaderList = quarterYearHeaderList.map(item => {
      const dataForQuarter = allQuarterData[item.quarterText];
      return {
        ...item,
        data: dataForQuarter,
      };
    });
    setQuarterYearRendering(updatedQuarterYearHeaderList);
  };

  const onDropDownQuarterChanged = (quarter, index) => {
    const modifiedQuarterYearData = quarterYearRendering.map(val => {
      if (val.id === index) {
        return { ...val, quarterText: quarter };
      } else {
        return val;
      }
    });
    setQuarterYearRendering(modifiedQuarterYearData);
    onViewClick(modifiedQuarterYearData[index]);
  };

  const onDropDownYearChanged = (year, index) => {
    const modifiedQuarterYearData = quarterYearRendering.map(val => {
      if (val.id === index) {
        return { ...val, yearText: year };
      } else {
        return val;
      }
    });
    setQuarterYearRendering(modifiedQuarterYearData);
    onViewClick(modifiedQuarterYearData[index]);
  };

  const isFutureQuarter = (quarterSent, yearSent) => {
    const quarterMap = {
      Q1: 1,
      Q2: 2,
      Q3: 3,
      Q4: 4,
    };
    const fiscalYear = parseInt(quarterInfo.fiscal_year, 10);
    const year = parseInt(yearSent, 10);

    if (
      fiscalYear > year ||
      (fiscalYear === year && quarterMap[quarterInfo.quarter] >= quarterMap[quarterSent])
    ) {
      return false;
    } else {
      return true;
    }
  };

  const isCurrentQuarter = (quarterSent, yearSent) => {
    if (quarterInfo.quarter === quarterSent && quarterInfo.fiscal_year === yearSent) {
      return true;
    } else {
      return false;
    }
  };

  const onViewClick = async quarterJsonData => {
    appInsights.trackTrace({
      message: JSON.stringify({
        message: `Calling getDashboardSingleQuartersData with reqPayload : ${ROLE_CONST.PL} ${currentUser.email} ${quarterJsonData.yearText} ${quarterJsonData.quarterText} ${systemDateTime.split('T')[0]}`,
        userId: localStorage.getItem('userEmail'),
        source: SOURCE.FRONTEND,
      }),
      severityLevel: 1, // 1 represents response log
    });

    const allQuarterData = await getDashboardSingleQuartersData(
      ROLE_CONST.PL,
      currentUser.email,
      quarterJsonData.yearText,
      quarterJsonData.quarterText,
      systemDateTime.split('T')[0],
      null, // ACCOUNT_ID
      null, // SERVICE
    );

    const targetQuarterData = allQuarterData[quarterJsonData.quarterText];
    updateQuarterYearData({ ...quarterJsonData, data: targetQuarterData });
  };

  const updateQuarterYearData = updateQuarter => {
    const updatedQuarterYearData = quarterYearRendering.map(val => {
      if (val.id === updateQuarter.id) {
        return { ...updateQuarter };
      } else {
        return val;
      }
    });

    setQuarterYearRendering(updatedQuarterYearData);
  };

  return (
    <ContainerWrapper>
      {quarterYearRendering !== null
        ? quarterYearRendering.map((item, index) => (
            <QuarterDetailsContainer key={index}>
              <SubHeaderContainer>
                <TitleContainer>
                  <StyledHeader>
                    Quarter {item.quarterText ? trimText(item.quarterText) : ''} | FY{' '}
                    {item.yearText}
                  </StyledHeader>

                  {isCurrentQuarter(item.quarterText, item.yearText) ? <CurrentPill /> : null}
                  {isFutureQuarter(item.quarterText, item.yearText) ? (
                    <FuturePillDashboard />
                  ) : null}
                </TitleContainer>

                <DropDownSelect
                  label="Quarter"
                  options={QUARTER_OPTIONS_LIST}
                  defaultValue={item.quarterText}
                  onDropDownValueChanged={quarter => {
                    onDropDownQuarterChanged(quarter, index, item);
                  }}
                />
                <DropDownSelect
                  label="Fiscal Year"
                  options={yearOptionsList}
                  defaultValue={currentYear.toString()}
                  onDropDownValueChanged={year => {
                    onDropDownYearChanged(year, index, item);
                  }}
                />
                {item?.data?.['quarterlyCalculation']?.actual_period === 'current' ? (
                  <MotifButton
                    style={buttonStyle}
                    onClick={() => {
                      navigate('/forecast');
                    }}
                  >
                    {BUTTON_TITLE.FORECAST}
                  </MotifButton>
                ) : item?.data?.['quarterlyCalculation']?.actual_period === 'past' ? (
                  <MotifButton
                    variant="secondary"
                    style={buttonStyle}
                    onClick={() => {
                      navigate('/forecast/past', {
                        state: {
                          year: item.yearText,
                          quarter: item.quarterText,
                          month: 'NA',
                          email: currentUser.email,
                          forecast_type: 'quarter',
                        },
                      });
                    }}
                    aria-label={`View details of Quarter ${item.quarterText} FY ${item.yearText}`}
                    disabled={
                      item?.data?.['quarterlyCalculation']?.tag ===
                      MONTH_QUARTER_STATUS.NOT_APPLICABLE
                    }
                  >
                    <span aria-hidden="true">{BUTTON_TITLE.VIEW}</span>
                  </MotifButton>
                ) : null}
              </SubHeaderContainer>
              <MonthlyTitleContainer>{QUARTERLY_DETAILS.FORECAST_SUMMARY}</MonthlyTitleContainer>

              {item?.data ? (
                <>
                  {isFutureQuarter(item?.quarterText, item?.yearText) ? (
                    <LegendFutureContainer>
                      <LabelStyle>
                        <StyledSpanContainer>
                          {formatNumberWithKAndM(item?.data['quarterlyCalculation'].commit)}{' '}
                        </StyledSpanContainer>
                        <RowStyle>
                          <LegendTextStyle>{QUARTERLY_DETAILS.PROJECTED_FORECAST}</LegendTextStyle>
                        </RowStyle>
                      </LabelStyle>
                      <LabelStyle>
                        <StyledSpanContainer>
                          {formatNumberWithKAndM(item?.data['quarterlyCalculation'].noCommit)}{' '}
                        </StyledSpanContainer>
                        <RowStyle>
                          <LegendTextStyle>{QUARTERLY_DETAILS.UNCOMMITED_PIPELINE}</LegendTextStyle>
                        </RowStyle>
                      </LabelStyle>
                    </LegendFutureContainer>
                  ) : null}

                  {!isFutureQuarter(item?.quarterText, item?.yearText) ? (
                    <div style={{ marginLeft: 20 }}>
                      <Legend
                        quarterlyData={item?.data['quarterlyCalculation']}
                        quarterText={item.quarterText}
                        flagImageSrc={flagIcon}
                        quarterTitle={null}
                      />
                    </div>
                  ) : null}

                  <MonthlyTitleContainer style={{ backgroundColor: '#F6F6FA' }}>
                    {QUARTERLY_DETAILS.MONTHLY_DETAILS}
                  </MonthlyTitleContainer>
                  <MonthlyDetails
                    quarterData={item}
                    email={currentUser.email}
                    account={currentUser.account_id}
                  />
                </>
              ) : null}
            </QuarterDetailsContainer>
          ))
        : null}
    </ContainerWrapper>
  );
};

export default QuarterDetails;
